import { useEffect, useState } from "react";
import FayettevilleHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const navItems = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "Facilities", href: "#facilities" },
  { label: "Compare", href: "#compare" },
  { label: "FAQ", href: "#faq" },
  { label: "Resources", href: "#resources" },
];

const faqs = [
  { q: "How fast can I get a passport in Fayetteville, AR?", a: "Our emergency service can have your passport ready in as little as 24 hours. Standard expedited processing takes 3–5 business days, and our budget option takes 7–10 business days. For UA students with study abroad programs, we recommend starting the process at least 4–6 weeks before your program starts." },
  { q: "Where can I apply for a passport in Fayetteville, AR?", a: "The main facility is the Fayetteville Main Post Office on East Township Street. There's also an acceptance facility at the Washington County Courthouse. We guide you to the location with the shortest current wait time." },
  { q: "Can Walmart employees at the Bentonville headquarters get expedited passports?", a: "Absolutely. We regularly assist NWA corporate professionals — Walmart, Tyson, JB Hunt employees — with urgent international business travel needs. Our 24–48 hour service handles even the most time-sensitive requests." },
  { q: "Do I need an appointment at a Fayetteville, AR passport facility?", a: "Most Fayetteville-area acceptance facilities are walk-in only. We'll tell you exactly when to arrive to minimize wait time and which locations are least busy." },
  { q: "What documents do I need for a first-time passport in Fayetteville, AR?", a: "You'll need proof of U.S. citizenship (birth certificate with raised seal), a government-issued photo ID, a compliant passport photo, and the completed DS-11 form. We provide a complete checklist and review everything before you visit." },
  { q: "How much does passport expediting cost from Fayetteville, AR?", a: "Government fees are $130–$190. Our service fees start at $99. Emergency 24-hour service is available at higher tiers. We guarantee the best price — find a lower rate and we'll match it." },
  { q: "Can University of Arkansas students get passports quickly?", a: "Yes, and we help many UA Razorback students each semester. We know the study abroad timeline, can rush applications during registration periods, and work with your university's international programs office requirements." },
  { q: "Is there a Regional Passport Agency near Fayetteville, AR?", a: "The nearest Regional Passport Agency is in Houston. For routine expedited service, our courier service is faster and more convenient — no 3-hour drive required." },
];

const destinations = [
  { name: "Barcelona", country: "Spain", flag: "🇪🇸", desc: "Top study abroad and tourism destination from NWA. UA students frequent this European gem." },
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Most popular spring break destination. Direct flights via Dallas make it a quick getaway." },
  { name: "London", country: "UK", flag: "🇬🇧", desc: "Business travel hub for Walmart international suppliers and UA exchange programs." },
  { name: "Tokyo", country: "Japan", flag: "🇯🇵", desc: "Growing destination for JB Hunt and Tyson Foods international business travel." },
  { name: "Patagonia", country: "Argentina/Chile", flag: "🇦🇷", desc: "Adventure travel hotspot for outdoor enthusiasts in the Ozarks community." },
  { name: "Cruise Ports", country: "Various", flag: "🚢", desc: "Galveston and New Orleans cruise ports are most popular from NWA families." },
];

const facilities = [
  { name: "Fayetteville Main Post Office", address: "915 E Township St, Fayetteville, AR 72703", phone: "(479) 442-9234", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "20–45 min" },
  { name: "Washington County Courthouse", address: "280 N College Ave, Fayetteville, AR 72701", phone: "(479) 444-1506", hours: "Mon–Fri 8:00 AM – 4:30 PM", wait: "15–30 min" },
  { name: "Springdale Post Office", address: "703 W Emma Ave, Springdale, AR 72764", phone: "(479) 756-3161", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "15–25 min" },
];

const testimonials = [
  { name: "Alex P.", city: "Dickson Street, Fayetteville", stars: 5, text: "My passport expired right before my Barcelona study abroad semester. Got it in 4 days. Go Hogs!" },
  { name: "Michelle W.", city: "Bentonville", stars: 5, text: "Walmart corporate trip to China with 2 weeks notice. These guys made it happen. Absolutely critical service for NWA business professionals." },
  { name: "David L.", city: "Fayetteville, AR", stars: 5, text: "As a JB Hunt logistics manager I travel internationally constantly. Fast Passport Center is my go-to for every renewal." },
];

export default function FayettevilleARPage() {
  useEffect(() => {
    document.title = "Emergency Passport Fayetteville, AR — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    setLink("canonical", "https://fastpassportcenter.com/fayetteville-ar/");
    setMeta("description", "Emergency and expedited passport services in Fayetteville, AR — as fast as 24 hours. Serving UA students, Walmart corporate travelers, and NWA families. A+ BBB rated.");
    setMeta("geo.placename", "Fayetteville, Arkansas");
    setMeta("geo.region", "US-AR");
    setMeta("geo.position", "36.0626;-94.1574");
    setMeta("ICBM", "36.0626, -94.1574");
    return () => { document.querySelector('link[rel="canonical"]')?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <FayettevilleHeroSection />
      <StickyNav />
      <ProcessingSection />
      <ServicesSection />
      <LocalAreaSection />
      <TravelTimingSection />
      <DestinationsSection />
      <TestimonialsSection />
      <AcceptanceFacilitiesSection />
      <ComparisonSection />
      <MistakesSection />
      <HowItWorksSection />
      <FAQSection />
      <HelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/fayetteville-ar" state="AR" />
      <FinalCTA />
    </div>
  );
}

function StickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = navItems.map((n) => document.querySelector(n.href));
      const scroll = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.getBoundingClientRect().top + window.scrollY <= scroll) { setActive(navItems[i].href); return; }
      }
      setActive("");
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {navItems.map((item) => (<a key={item.href} href={item.href} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active === item.href ? "bg-[#1A365D] text-white" : "text-gray-600 hover:bg-gray-100"}`}>{item.label}</a>))}
        </div>
      </div>
    </div>
  );
}

function ProcessingSection() {
  return (
    <section id="processing" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Fayetteville, AR?</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">Three service tiers for every timeline and budget in Northwest Arkansas.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $299", badge: "Fastest", desc: "For corporate travelers and study abroad emergencies. Hand-carry courier to the State Department.", features: ["24-hour turnaround available", "Hand-carry courier service", "Real-time tracking", "Phone support 7 days/week"] },
            { title: "Expedited", time: "3–5 Business Days", price: "From $149", badge: "Most Popular", desc: "Perfect for UA students and NWA families with upcoming travel plans.", features: ["3–5 day guaranteed delivery", "Application review included", "Photo compliance check", "Email + phone support"] },
            { title: "Standard", time: "7–10 Business Days", price: "From $99", badge: "Best Value", desc: "For travelers with a few weeks before departure. Still far faster than government standard.", features: ["7–10 day processing", "Complete application guidance", "Document checklist", "Email support"] },
          ].map((plan, i) => (
            <div key={i} className={`bg-white border rounded-xl p-7 flex flex-col ${plan.badge === "Most Popular" ? "border-[#F6C344] ring-1 ring-[#F6C344]" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${plan.badge === "Fastest" ? "bg-red-100 text-red-700" : plan.badge === "Most Popular" ? "bg-[#F6C344]/20 text-[#1A365D]" : "bg-gray-100 text-gray-700"}`}>{plan.badge}</span>
                <span className="text-[#1A365D] font-bold text-lg">{plan.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-xl mb-1">{plan.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{plan.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">{plan.desc}</p>
              <ul className="space-y-2 mb-6">{plan.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>{f}</li>)}</ul>
              <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0f2341] text-white font-semibold text-sm py-3 rounded-lg transition-all whitespace-nowrap">Get Started</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "New Passport", desc: "First-time application assistance for UA students and NWA residents." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for expired or soon-to-expire passports." },
    { icon: "ri-alert-line", title: "Lost/Stolen", desc: "Emergency replacement with DS-64 form guidance." },
    { icon: "ri-user-smile-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance." },
    { icon: "ri-time-line", title: "Emergency", desc: "24-hour processing for NWA corporate emergencies." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Post-marriage or legal name change updates." },
  ];
  return (
    <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Fayetteville, AR &amp; NWA Travelers</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className={`${s.icon} text-[#1A365D] text-xl`}></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalAreaSection() {
  return (
    <section id="local-area" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Fayetteville, Arkansas</h2>
        </div>
        <div className="max-w-3xl mx-auto text-gray-600 text-base leading-relaxed space-y-5">
          <p>Fayetteville, home to the University of Arkansas and nestled in the Ozark Mountains, is the cultural and educational heart of Northwest Arkansas. With over 90,000 residents and an MSA that includes Springdale, Rogers, and Bentonville, the greater Fayetteville metro is one of the fastest-growing regions in the country — and a major generator of international passport demand.</p>
          <p>The University of Arkansas alone has over 33,000 students, many of whom participate in international study abroad programs, research exchanges, and global competitions. The UA Abroad program sends hundreds of students to Europe, Latin America, and Asia each semester, creating a constant demand for expedited passport services, especially during the August and January departure peaks. Faculty and researchers also frequently need passports for international conferences, Fulbright grants, and academic partnerships.</p>
          <p>Beyond the university, Northwest Arkansas is home to some of America's largest corporations. Walmart's global headquarters in Bentonville employs thousands of international business travelers. Tyson Foods, JB Hunt Transport, and Dillard's all have significant international operations requiring frequent passport renewals and emergency processing. Local suppliers and vendors attending Walmart's Open Call events also regularly need fresh passports on short notice.</p>
          <p>Fayetteville's outdoor community — mountain bikers, hikers, and adventure travelers — frequently plan international trips to Patagonia, Peru, and New Zealand, generating a steady stream of passport demand year-round. The Northwest Arkansas Regional Airport (XNA) provides connections through Dallas, Chicago, and Atlanta, making international travel increasingly accessible for Fayetteville residents. Whether you're a freshman headed to Barcelona, a Walmart buyer flying to Shenzhen, or a family planning a Caribbean cruise, Fast Passport Center has the expertise and speed to get your passport ready when you need it.</p>
        </div>
      </div>
    </section>
  );
}

function TravelTimingSection() {
  return (
    <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">When Fayetteville, AR Travelers Need Passports</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { month: "August–September", title: "Fall Study Abroad Peak", desc: "UA students departing for semester abroad programs. Rush orders spike at the start of the fall semester." },
            { month: "January–February", title: "Spring Study Abroad", desc: "Second wave of UA study abroad departures. Spring break planning also begins." },
            { month: "March–April", title: "Spring Break Rush", desc: "Cancun, Jamaica, and Caribbean destinations peak. NWA families book beach trips." },
            { month: "May–June", title: "Corporate Travel Season", desc: "Walmart global supplier meetings and summer corporate travel surge." },
            { month: "July–August", title: "Summer Travel Peak", desc: "Highest international travel volume. European family trips and adventure travel." },
            { month: "November–December", title: "Holiday Travel", desc: "Winter cruise bookings and holiday international travel. Renewal rush before year-end." },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-wider mb-2">{t.month}</p>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{t.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Fayetteville, AR &amp; NWA Travelers Go</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((d, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{d.flag}</span>
                <div><h3 className="font-bold text-[#1A365D] text-lg">{d.name}</h3><p className="text-gray-400 text-xs">{d.country}</p></div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Fayetteville, AR Travelers Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-1 mb-3">{[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EEF2F8] flex items-center justify-center"><i className="ri-user-line text-[#1A365D]"></i></div>
                <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.city}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcceptanceFacilitiesSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="facilities" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Acceptance Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Fayetteville, AR</h2>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto">
          {facilities.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-map-pin-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><p className="text-gray-400 text-xs">{f.address}</p></div>
                </div>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl`}></i>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Phone</p><p className="text-[#1A365D] text-sm font-medium">{f.phone}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Hours</p><p className="text-[#1A365D] text-sm font-medium">{f.hours}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Avg Wait</p><p className="text-[#1A365D] text-sm font-medium">{f.wait}</p></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    { feature: "Processing Speed", us: "As fast as 24 hours", them: "10–13 weeks standard" },
    { feature: "Office Visit Required", us: "No — mail or drop-off", them: "Yes — in-person required" },
    { feature: "Photo Review", us: "Included — we verify compliance", them: "Self-service — rejections common" },
    { feature: "Application Review", us: "Expert review before submission", them: "No review — errors cause delays" },
    { feature: "Tracking", us: "Real-time status updates", them: "Limited status info" },
    { feature: "Customer Support", us: "Phone + email, 7 days/week", them: "Phone only, limited hours" },
    { feature: "Price Guarantee", us: "Best price guarantee", them: "No price matching" },
    { feature: "Acceptance Facility Help", us: "We find the shortest wait times", them: "Self-schedule at any facility" },
    { feature: "Refund Policy", us: "Satisfaction guarantee", them: "No refunds after submission" },
  ];
  return (
    <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. DIY Government Processing</h2>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 border-b border-gray-200 bg-[#EEF2F8]">
            <div className="p-4 font-bold text-[#1A365D] text-sm">Feature</div>
            <div className="p-4 font-bold text-[#1A365D] text-sm text-center">Fast Passport Center</div>
            <div className="p-4 font-bold text-gray-500 text-sm text-center">Government Standard</div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 ${i !== rows.length - 1 ? "border-b border-gray-100" : ""}`}>
              <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
              <div className="p-4 text-sm text-[#1A365D] font-semibold text-center bg-[#F7F8FA]/50">{row.us}</div>
              <div className="p-4 text-sm text-gray-500 text-center">{row.them}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MistakesSection() {
  const mistakes = [
    { title: "Study Abroad Last Minute", desc: "UA students often wait until 3 weeks before departure to start the passport process. Start as soon as you're accepted." },
    { title: "Wrong Photo", desc: "Photos from campus CVS are often rejected. We verify your photo specs before you submit." },
    { title: "Expired Identification", desc: "Some students try to use expired student IDs at acceptance facilities. Only valid government photo ID is accepted." },
    { title: "Corporate Booking Before Passport", desc: "NWA corporate travelers sometimes book non-refundable international flights before checking passport expiration dates." },
    { title: "Wrong Application Form", desc: "DS-11 vs DS-82 confusion is common. We tell you exactly which form based on your passport history." },
  ];
  return (
    <section id="mistakes" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Mistakes Fayetteville, AR Applicants Make</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-4"><i className="ri-close-circle-line text-red-500 text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Choose Your Service", desc: "Pick emergency, expedited, or standard based on your travel timeline." },
    { num: "02", title: "Complete Application", desc: "We walk you through DS-11 or DS-82 step by step." },
    { num: "03", title: "Visit Acceptance Facility", desc: "We direct you to the Fayetteville, AR facility with the shortest wait." },
    { num: "04", title: "We Handle Processing", desc: "Your application is couriered to the State Department for priority processing." },
    { num: "05", title: "Receive Your Passport", desc: "Secure delivery. Real-time tracking throughout." },
  ];
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">How It Works</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Steps to Your Passport</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-lg mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fayetteville, AR Passport Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl flex-shrink-0`}></i>
              </button>
              {open === i && <div className="px-5 pb-5 pt-0 border-t border-gray-100"><p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HelpfulResourcesSection() {
  return (
    <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Fayetteville, AR Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Travel.State.Gov", desc: "Official U.S. Department of State passport information.", link: "https://travel.state.gov/content/travel/en/passports.html" },
            { title: "XNA Airport", desc: "Northwest Arkansas National Airport — flights to Dallas, Chicago, Atlanta.", link: "https://www.flyxna.com/" },
            { title: "UA Study Abroad", desc: "University of Arkansas international programs office.", link: "https://studyabroad.uark.edu/" },
          ].map((r, i) => (
            <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
              <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-[#1A365D]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport in Fayetteville, AR?</h2>
        <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">Whether you're a Razorback headed abroad or a corporate traveler flying to Bentonville's global partners, we've got you covered.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
        <p className="text-white/50 text-sm mt-6">Call us at <a href="tel:8772530084" className="text-[#F6C344] hover:underline">877-253-0084</a> for immediate assistance</p>
      </div>
    </section>
  );
}